import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { logout } from "@/lib/api";
import { toast } from "sonner";
import {
  SignOut, Copy, Users, Tag,
  DiscordLogo, LockKey, Wallet, WarningCircle,
} from "@phosphor-icons/react";
import { CREATOR } from "@/constants/testIds";

function StatBlock({ label, value, accent = "#00F0FF", testid, sub }) {
  return (
    <div className="card-tactical p-6" data-testid={testid}>
      <div className="kbd-label">{label}</div>
      <div className="stat-value text-4xl mt-2" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-xs text-white/40 font-mono mt-1">{sub}</div>}
    </div>
  );
}

export default function CreatorDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [summary, setSummary] = useState(null);
  const [payout, setPayout] = useState({ method: "venmo", handle: "", phone: "", note: "" });
  const [savingPayout, setSavingPayout] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, s] = await Promise.all([
          api.get("/creator/me"),
          api.get("/creator/summary"),
        ]);
        setMe(m.data); setSummary(s.data);
        if (m.data.payout_details) {
          const p = m.data.payout_details;
          setPayout({ method: p.method || "venmo", handle: p.handle || "", phone: p.phone || "", note: p.note || "" });
        }
      } catch (e) {
        toast.error("Session expired");
        logout(); nav("/creator/login");
      }
    })();
  }, [nav]);

  async function savePayout() {
    if (!payout.method) return toast.error("Pick a payout method");
    setSavingPayout(true);
    try {
      const r = await api.put("/creator/payout", payout);
      setMe(r.data);
      toast.success("Payout details saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSavingPayout(false); }
  }

  function copyToClipboard(txt, label = "Link") {
    navigator.clipboard.writeText(txt);
    toast.success(`${label} copied`);
  }

  if (!me || !summary) return <div className="min-h-screen flex items-center justify-center kbd-label text-neon-cyan">// LOADING</div>;

  const shareUrl = me.discord_invite_url || null;
  const published = summary.earnings_published;
  const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const pd = me.payout_details;
  const hasPayout = !!(pd && pd.method && (pd.handle || pd.phone));
  const METHODS = [["venmo", "Venmo"], ["paypal", "PayPal"], ["cashapp", "Cash App"], ["zelle", "Zelle"], ["wise", "Wise"], ["ach", "ACH / Bank"]];
  const handleLabel = { venmo: "@username", cashapp: "$cashtag", paypal: "PayPal email or @handle", zelle: "Zelle email or phone", wise: "Wise email or @tag", ach: "Account holder name" }[payout.method] || "Username / handle";

  return (
    <div className="min-h-screen" data-testid={CREATOR.dashboard}>
      <header className="border-b border-white/10 bg-obsidian sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="kbd-label text-neon-yellow">CREATOR CONSOLE</div>
            <div className="display-heading font-black text-xl">{me.display_name}</div>
          </div>
          <button onClick={() => { logout(); nav("/"); }} data-testid={CREATOR.logout} className="ghost-btn px-4 py-2 kbd-label inline-flex items-center gap-2 text-white">
            <SignOut size={16} /> LOGOUT
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {!hasPayout && (
          <div className="card-tactical p-4 border-l-4 border-l-neon-yellow flex items-center gap-3" data-testid="payout-banner">
            <WarningCircle size={22} color="#E4FF00" weight="fill" />
            <div className="text-sm font-mono text-white/80">Add your <b className="text-white">payout details</b> below so your manager can pay you.</div>
          </div>
        )}
        {/* Hero: primary referral link — the creator's Discord invite */}
        <section className="card-tactical p-6 md:p-8 border-l-4 border-l-neon-cyan">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-neon-cyan flex items-center justify-center shrink-0">
              <DiscordLogo size={24} weight="fill" color="#050505" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="kbd-label text-neon-cyan">YOUR REFERRAL LINK · PASTE THIS IN YOUR BIO</div>
              {shareUrl ? (
                <>
                  <div className="mt-2 flex flex-col md:flex-row gap-3 md:items-center">
                    <div className="flex-1 font-mono text-lg md:text-xl bg-black/50 border border-white/10 px-4 py-3 truncate select-all" data-testid="creator-primary-link">
                      {shareUrl}
                    </div>
                    <button onClick={() => copyToClipboard(shareUrl, "Referral link")} data-testid="creator-primary-link-copy"
                      className="neon-btn px-5 py-3 inline-flex items-center justify-center gap-2 whitespace-nowrap">
                      <Copy size={16} weight="bold" /> COPY LINK
                    </button>
                  </div>
                  <div className="mt-3 text-xs text-white/50 font-mono">
                    Share this Discord invite directly — joins are automatically tracked to you.
                  </div>
                </>
              ) : (
                <div className="mt-2 text-sm text-white/60 font-mono">
                  Your server owner hasn't set your Discord invite link yet. Ask them to add it so you can start referring.
                </div>
              )}
            </div>
          </div>
        </section>

        {me.promo_code && (
          <section className="card-tactical p-6 md:p-8 border-l-4 border-l-neon-yellow" data-testid="creator-promo-offer">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-neon-yellow flex items-center justify-center shrink-0">
                <Tag size={24} weight="fill" color="#050505" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="kbd-label text-neon-yellow">YOUR DISCOUNT CODE · SHARE WITH YOUR AUDIENCE</div>
                <div className="mt-2 text-lg md:text-xl font-mono">
                  Use code <span className="text-neon-yellow font-black tracking-wide">{me.promo_code}</span>
                  {me.promo_description ? <> to get <span className="text-white font-bold">{me.promo_description}</span></> : null}
                </div>
                <div className="mt-3 flex flex-col md:flex-row gap-3 md:items-center">
                  <div className="flex-1 font-mono text-lg bg-black/50 border border-white/10 px-4 py-3 truncate select-all" data-testid="creator-promo-code">{me.promo_code}</div>
                  <button onClick={() => copyToClipboard(me.promo_code, "Promo code")} data-testid="creator-promo-code-copy"
                    className="neon-btn px-5 py-3 inline-flex items-center justify-center gap-2 whitespace-nowrap">
                    <Copy size={16} weight="bold" /> COPY CODE
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Your numbers */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatBlock label="DISCORD MEMBERS" value={summary.current_members} accent="#E4FF00" testid="creator-members-count" sub="Currently in the server via your link" />
          {published ? (
            <>
              <StatBlock label="EARNED THIS MONTH" value={money(summary.earned_this_month)} accent="#22C55E" testid="creator-earned-month" sub={`${summary.commission_pct}% commission`} />
              <StatBlock label="EARNED ALL-TIME" value={money(summary.earned_all_time)} accent="#22C55E" testid="creator-earned-total" sub="Net of fees & tax" />
            </>
          ) : (
            <div className="card-tactical p-6 md:col-span-2 flex items-center gap-4" data-testid="creator-earnings-pending">
              <div className="w-10 h-10 bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                <LockKey size={20} color="#A1A1AA" />
              </div>
              <div>
                <div className="kbd-label text-white/60">EARNINGS · PENDING REVIEW</div>
                <div className="text-sm text-white/50 font-mono mt-1">Your manager is finalizing your payout figures. They'll appear here once published.</div>
              </div>
            </div>
          )}
        </section>

        {/* Payout details — creator-editable, owner-visible */}
        <section className="card-tactical p-6" data-testid="creator-payout-section">
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={20} color="#E4FF00" weight="fill" />
            <h2 className="display-heading font-bold text-lg">PAYOUT DETAILS</h2>
          </div>
          <p className="text-xs text-white/50 font-mono mb-4">How you'd like to be paid. Kept private — only your manager sees this.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="kbd-label mb-1">METHOD</div>
              <select value={payout.method} onChange={(e) => setPayout({ ...payout, method: e.target.value })} data-testid="payout-method"
                className="w-full bg-black/60 border border-white/10 px-3 py-3 font-mono text-sm focus:border-neon-cyan outline-none">
                {METHODS.map(([v, l]) => <option key={v} value={v} className="bg-obsidian">{l}</option>)}
              </select>
            </div>
            <div>
              <div className="kbd-label mb-1">USERNAME / HANDLE</div>
              <input value={payout.handle} onChange={(e) => setPayout({ ...payout, handle: e.target.value })} placeholder={handleLabel} data-testid="payout-handle"
                className="w-full bg-black/60 border border-white/10 px-3 py-3 font-mono text-sm focus:border-neon-cyan outline-none" />
            </div>
            <div>
              <div className="kbd-label mb-1">PHONE #</div>
              <input value={payout.phone} onChange={(e) => setPayout({ ...payout, phone: e.target.value })} placeholder="e.g. +1 555 123 4567" data-testid="payout-phone"
                className="w-full bg-black/60 border border-white/10 px-3 py-3 font-mono text-sm focus:border-neon-cyan outline-none" />
            </div>
            <div>
              <div className="kbd-label mb-1">NOTE (OPTIONAL)</div>
              <input value={payout.note} onChange={(e) => setPayout({ ...payout, note: e.target.value })} placeholder="anything your manager should know" data-testid="payout-note"
                className="w-full bg-black/60 border border-white/10 px-3 py-3 font-mono text-sm focus:border-neon-cyan outline-none" />
            </div>
          </div>
          <button onClick={savePayout} disabled={savingPayout} data-testid="payout-save" className="neon-btn px-6 py-3 mt-4 disabled:opacity-50">
            {savingPayout ? "SAVING…" : "SAVE PAYOUT DETAILS"}
          </button>
          {pd?.updated_at && <div className="text-[10px] text-white/40 font-mono mt-2">Last updated {pd.updated_at.slice(0, 10)}</div>}
        </section>
      </main>
    </div>
  );
}
